// apps/web/src/components/brand/brand-glyph.tsx

import { Box, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BrandGlyphKey } from "@/lib/brand-keys";
import { VENDORED_BRAND_GLYPHS } from "./brand-glyphs.generated";

const BRAND_LABELS: Record<BrandGlyphKey, string> = {
  anthropic: "Anthropic",
  claude: "Claude",
  cloudflare: "Cloudflare",
  cohere: "Cohere",
  cursor: "Cursor",
  deepseek: "DeepSeek",
  gemini: "Gemini",
  gemma: "Gemma",
  github: "GitHub",
  google: "Google",
  "google-drive": "Google Drive",
  grok: "Grok",
  hermes: "Hermes Agent",
  linear: "Linear",
  meta: "Meta",
  microsoft: "Microsoft",
  mistral: "Mistral",
  model: "Model",
  notion: "Notion",
  openai: "OpenAI",
  openclaw: "OpenClaw",
  perplexity: "Perplexity",
  pi: "Pi",
  qwen: "Qwen",
  slack: "Slack",
  whatsapp: "WhatsApp",
};

// LAT-1268 — `linear` and `google-drive` now vendor real brand marks, so their
// Cable-plug stopgaps are retired. A brand key without a vendored mark falls to
// the neutral Box tile, never a plug — so a future missing mark fails loudly to
// a blank tile rather than silently mislabeling a connector.
const FALLBACK_ICONS: Partial<Record<BrandGlyphKey, LucideIcon>> = {
  model: Box,
};

export interface BrandGlyphProps {
  brand: BrandGlyphKey;
  label?: string;
  hostLabel?: string | null;
  size?: "xs" | "sm" | "md";
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<BrandGlyphProps["size"]>, string> = {
  xs: "size-3",
  sm: "size-4",
  md: "size-5",
};

export function BrandGlyph({
  brand,
  label,
  hostLabel,
  size = "sm",
  className,
}: BrandGlyphProps) {
  const accessibleLabel = label ?? BRAND_LABELS[brand];
  const title = hostLabel
    ? `${accessibleLabel} · hosted on ${hostLabel}`
    : accessibleLabel;
  const svg =
    VENDORED_BRAND_GLYPHS[brand as keyof typeof VENDORED_BRAND_GLYPHS];
  const baseClassName = cn(
    "inline-flex shrink-0 items-center justify-center text-muted-foreground [&>svg]:block [&>svg]:size-full",
    SIZE_CLASS[size],
    className,
  );

  if (svg) {
    return (
      <span
        aria-label={accessibleLabel}
        className={baseClassName}
        data-brand={brand}
        dangerouslySetInnerHTML={{ __html: svg }}
        role="img"
        title={title}
      />
    );
  }

  const Icon = FALLBACK_ICONS[brand] ?? Box;
  return (
    <span
      aria-label={accessibleLabel}
      className={baseClassName}
      data-brand={brand}
      role="img"
      title={title}
    >
      <Icon aria-hidden="true" className="size-full" />
    </span>
  );
}

export function ModelGlyph(props: BrandGlyphProps) {
  return <BrandGlyph {...props} />;
}
